import type { Cents, ContractTerm, Iso, StepState } from "./index.js";
import type { Dealer, DealerModel } from "./index.js";

import type { HouseDealerSetting } from "./settings.js";
import type { OrderLineNumber } from "./billing-requirements.js";

// ---------- partners & settings ----------

export type OrderPartner = "house" | "dealer" | "reseller";

/**
 * Resolves an order's partner kind:
 * - "house": dealer is the configured house dealer
 * - "reseller": dealer model is wholesale
 * - "dealer": dealer model is agent
 */
export function resolveOrderPartner(
  dealer: Pick<Dealer, "id" | "model"> | { id: string; model: DealerModel },
  houseDealer: HouseDealerSetting | string | null | undefined,
): OrderPartner {
  const houseDealerId = typeof houseDealer === "string" ? houseDealer : houseDealer?.dealerId;
  if (houseDealerId && dealer.id === houseDealerId) {
    return "house";
  }
  return dealer.model === "wholesale" ? "reseller" : "dealer";
}

// ---------- tickets & plans ----------

export type TicketKind = "order" | "provisioning" | "source";

export interface OrderTicketLive {
  statusLabel: string;
  statusName?: string;
  checklist: { done: number; total: number; of?: number } | null;
}

export interface OrderTicket {
  kind: TicketKind;
  ticketId: number;
  ticketNumber: string;
  title: string;
  queueId: number;
  queueName: string | null;
  checklistLibraryId: number | null;
  checklistName: string | null;
  url: string;
  state: StepState;
  error: string | null;
  live?: OrderTicketLive | null;
}

export interface OrderPatch {
  dueDate?: string | null;
  numbersNote?: string;
  /**
   * Each line's Telephone Number, as the seller sets it (#1763). Each entry sets its line's number, 10 digits, and an
   * empty one takes it off; lines not listed keep theirs. Only lines whose OneBill plan needs a Telephone Number take one,
   * and only until billing is approved.
   */
  lineNumbers?: OrderLineNumber[];
  expectedUpdatedAt: Iso;
}

export interface TicketPlanConfig {
  statusId: number;
  priorityId: number;
  sourceId: number;
  ticketTypeId?: number;

  dealerOrderQueueId: number;
  orderIssueTypeId: number;
  orderSubIssueTypeId: number;
  orderCategoryId: number;

  houseOrderQueueId: number;
  houseOrderChecklistId: number | null;

  resellerOrderQueueId: number;

  provisioningQueueId: number;
  provisioningIssueTypeId: number;
  provisioningSubIssueTypeId: number;
  provisioningChecklistId: number;
  provisioningCategoryId?: number;
}

export interface PlannedTicket {
  kind: "order" | "provisioning";
  title: string;
  queueId: number;
  issueTypeId: number;
  subIssueTypeId: number;
  categoryId: number;
  checklistLibraryId: number | null;
  statusId: number;
  priorityId: number;
  sourceId: number;
  ticketTypeId?: number;
}

export interface TicketPlanOrderInput {
  number: string;
  customerName: string;
  quoteNumber: string;
  kind?: "new" | "mac";
  ticketPlan?: "v1" | null;
}

/**
 * Pure rule for planning tickets created by an order.
 * All queue, checklist, and status IDs come from configuration.
 *
 * Rules:
 * - MAC orders open no new tickets (pdf/notes attach to source ticket).
 * - Orders created before this round (no ticketPlan or ticketPlan !== "v1") keep
 *   their single order ticket and get no provisioning ticket.
 * - New sale, house: Post Sale queue, New Contract checklist, plus provisioning ticket.
 * - New sale, dealer: Projects queue, no checklist, plus provisioning ticket.
 * - New sale, reseller: Billing queue, no checklist, no provisioning ticket.
 */
export function ticketPlan(
  order: TicketPlanOrderInput,
  partner: OrderPartner,
  config: TicketPlanConfig,
): PlannedTicket[] {
  if (order.kind === "mac") {
    return [];
  }

  const orderTicket = buildPlannedOrderTicket(order, partner, config);

  if (order.ticketPlan !== "v1") {
    return [orderTicket];
  }

  if (partner === "reseller") {
    return [orderTicket];
  }

  const provisioningTicket = buildPlannedProvisioningTicket(order, config);
  return [orderTicket, provisioningTicket];
}

function buildPlannedOrderTicket(
  order: TicketPlanOrderInput,
  partner: OrderPartner,
  config: TicketPlanConfig,
): PlannedTicket {
  if (partner === "house") {
    return {
      kind: "order",
      title: `SOLD: ${order.customerName} - Quote ${order.quoteNumber}`,
      queueId: config.houseOrderQueueId,
      issueTypeId: config.orderIssueTypeId,
      subIssueTypeId: config.orderSubIssueTypeId,
      categoryId: config.orderCategoryId,
      checklistLibraryId: config.houseOrderChecklistId,
      statusId: config.statusId,
      priorityId: config.priorityId,
      sourceId: config.sourceId,
      ticketTypeId: config.ticketTypeId,
    };
  }

  if (partner === "reseller") {
    return {
      kind: "order",
      title: `Omni order ${order.number}: ${order.customerName}`,
      queueId: config.resellerOrderQueueId,
      issueTypeId: config.orderIssueTypeId,
      subIssueTypeId: config.orderSubIssueTypeId,
      categoryId: config.orderCategoryId,
      checklistLibraryId: null,
      statusId: config.statusId,
      priorityId: config.priorityId,
      sourceId: config.sourceId,
      ticketTypeId: config.ticketTypeId,
    };
  }

  return {
    kind: "order",
    title: `Omni order ${order.number}: ${order.customerName}`,
    queueId: config.dealerOrderQueueId,
    issueTypeId: config.orderIssueTypeId,
    subIssueTypeId: config.orderSubIssueTypeId,
    categoryId: config.orderCategoryId,
    checklistLibraryId: null,
    statusId: config.statusId,
    priorityId: config.priorityId,
    sourceId: config.sourceId,
    ticketTypeId: config.ticketTypeId,
  };
}

function buildPlannedProvisioningTicket(
  order: TicketPlanOrderInput,
  config: TicketPlanConfig,
): PlannedTicket {
  return {
    kind: "provisioning",
    title: `PROVISIONING: ${order.customerName} - Omni order ${order.number}`,
    queueId: config.provisioningQueueId,
    issueTypeId: config.provisioningIssueTypeId,
    subIssueTypeId: config.provisioningSubIssueTypeId,
    categoryId: config.provisioningCategoryId ?? config.orderCategoryId,
    checklistLibraryId: config.provisioningChecklistId,
    statusId: config.statusId,
    priorityId: config.priorityId,
    sourceId: config.sourceId,
    ticketTypeId: config.ticketTypeId,
  };
}

// ---------- description builders & markers ----------

export interface TicketLineItem {
  qty: number;
  name: string;
  code?: string | null;
  unitPrice: Cents;
  billing: "monthly" | "one_time";
}

export interface OrderTicketDescriptionInput {
  orderNumber: string;
  quoteNumber: string;
  customerName: string;
  dealerName: string;
  repEmail: string;
  term?: string | ContractTerm | null;
  accountNumber?: string | null;
  dueDate?: string | null;
  numbersNote?: string | null;
  lines?: TicketLineItem[];
  provisioningTicketNumber?: string | null;
}

export interface ProvisioningTicketDescriptionInput {
  orderNumber: string;
  orderId?: string;
  customerName: string;
  dealerName?: string;
  accountNumber?: string | null;
  dueDate?: string | null;
  numbersNote?: string | null;
  lines?: TicketLineItem[];
  orderTicketNumber?: string | null;
  omniOrderUrl?: string | null;
}

export function buildOrderTicketDescription(input: OrderTicketDescriptionInput): string {
  const parts: string[] = [
    `Omni order ${input.orderNumber} (order)`,
    `Quote: ${input.quoteNumber}`,
    `Customer: ${input.customerName}`,
  ];

  if (input.accountNumber) {
    parts.push(`Account: ${input.accountNumber}`);
  }

  parts.push(`Dealer: ${input.dealerName}`);
  parts.push(`Sales Rep: ${input.repEmail}`);

  if (input.term) {
    const termStr =
      input.term === "month_to_month"
        ? "Month to month"
        : typeof input.term === "number"
          ? `${input.term} months`
          : input.term;
    parts.push(`Term: ${termStr}`);
  }

  if (input.dueDate) {
    parts.push(`Due Date: ${input.dueDate}`);
  }

  if (input.provisioningTicketNumber) {
    parts.push(`Provisioning Ticket: ${input.provisioningTicketNumber}`);
  }

  if (input.numbersNote) {
    parts.push("", "Numbers / Porting:", input.numbersNote);
  }

  if (input.lines && input.lines.length > 0) {
    parts.push("", "Lines:");
    for (const l of input.lines) {
      const codeStr = l.code ? ` (${l.code})` : "";
      const priceStr = `$${(l.unitPrice / 100).toFixed(2)}`;
      parts.push(`- ${l.qty}x ${l.name}${codeStr} @ ${priceStr} (${l.billing})`);
    }
  }

  return parts.join("\n");
}

export function buildProvisioningTicketDescription(input: ProvisioningTicketDescriptionInput): string {
  const parts: string[] = [
    `Omni order ${input.orderNumber} (provisioning)`,
  ];

  if (input.orderTicketNumber) {
    parts.push(`Order Ticket: ${input.orderTicketNumber}`);
  }

  if (input.omniOrderUrl) {
    parts.push(`Omni Order: ${input.omniOrderUrl}`);
  }

  parts.push(`Customer: ${input.customerName}`);

  if (input.accountNumber) {
    parts.push(`Account: ${input.accountNumber}`);
  }

  if (input.dealerName) {
    parts.push(`Dealer: ${input.dealerName}`);
  }

  if (input.dueDate) {
    parts.push(`Due Date: ${input.dueDate}`);
  }

  if (input.numbersNote) {
    parts.push("", "Numbers / Porting:", input.numbersNote);
  }

  if (input.lines && input.lines.length > 0) {
    parts.push("", "Lines:");
    for (const l of input.lines) {
      const codeStr = l.code ? ` (${l.code})` : "";
      const priceStr = `$${(l.unitPrice / 100).toFixed(2)}`;
      parts.push(`- ${l.qty}x ${l.name}${codeStr} @ ${priceStr} (${l.billing})`);
    }
  }

  return parts.join("\n");
}

/**
 * Returns the ticket description marker string.
 */
export function ticketMarker(orderNumber: string, kind: "order" | "provisioning"): string {
  return `Omni order ${orderNumber} (${kind})`;
}

/**
 * Returns the legacy ticket description marker string.
 */
export function oldTicketMarker(orderNumber: string): string {
  return `Lens order ${orderNumber}`;
}

/**
 * Inspects a ticket's description to match its order role:
 * - "Omni order O-n (provisioning)" -> "provisioning"
 * - "Omni order O-n (order)" -> "order"
 * - "Lens order O-n" -> "order"
 * - otherwise null
 */
export function matchTicketMarker(
  description: string | null | undefined,
  orderNumber: string,
): "order" | "provisioning" | null {
  if (!description) return null;
  if (description.includes(`Omni order ${orderNumber} (provisioning)`)) {
    return "provisioning";
  }
  if (description.includes(`Omni order ${orderNumber} (order)`)) {
    return "order";
  }
  if (description.includes(`Lens order ${orderNumber}`)) {
    return "order";
  }
  return null;
}

/**
 * Checks whether a ticket matches an order, either by kind or generally.
 * Matches both new "Omni order O-n" and legacy "Lens order O-n" markers.
 */
export function isOrderTicketMatch(
  ticket: { title?: string | null; description?: string | null },
  orderNumber: string,
  kind?: "order" | "provisioning",
): boolean {
  const desc = ticket.description ?? "";
  const title = ticket.title ?? "";

  if (kind === "provisioning") {
    return (
      desc.includes(`Omni order ${orderNumber} (provisioning)`) ||
      (title.includes("PROVISIONING:") && title.includes(`Omni order ${orderNumber}`))
    );
  }

  if (kind === "order") {
    if (desc.includes(`Omni order ${orderNumber} (order)`)) return true;
    if (desc.includes(`Lens order ${orderNumber}`)) return true;
    if (title.includes("SOLD:") && title.includes(orderNumber)) return true;
    if (title.includes(`Omni order ${orderNumber}`) && !title.includes("PROVISIONING:")) return true;
    if (title.includes(`Lens order ${orderNumber}`)) return true;
    return false;
  }

  return (
    matchTicketMarker(desc, orderNumber) !== null ||
    title.includes(`Omni order ${orderNumber}`) ||
    title.includes(`Lens order ${orderNumber}`)
  );
}

// ---------- port spec types ----------

export interface CreateTicketSpec {
  companyId: number;
  title: string;
  description: string;
  orderNumber?: string;
  queueId?: number;
  statusId?: number;
  priorityId?: number;
  sourceId?: number;
  issueTypeId?: number;
  subIssueTypeId?: number;
  categoryId?: number;
  ticketTypeId?: number;
  contactId?: number | null;
  dueDateTime?: string | null;
}

export interface FoundOrderTicket {
  kind: "order" | "provisioning";
  ticketId: number;
  ticketNumber: string;
  title?: string;
}

export interface TicketNoteInput {
  title: string;
  description: string;
  noteType?: number;
  publish?: number;
}
